// Specialized form component for Court Dimensions
// Supports flexible JSON structure: {Name: "Image", Value: [{title: "", url: "", Author: ""}]}

import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface CourtDimensionsFormProps {
  skillForm: any;
  setSkillForm: (form: any) => void;
}

export const CourtDimensionsForm: React.FC<CourtDimensionsFormProps> = ({
  skillForm,
  setSkillForm,
}) => {
  // Initialize content structure
  const getContentStructure = () => {
    if (!skillForm.content || typeof skillForm.content !== 'object') {
      return {
        Images: [],
        Videos: [],
        Text: [],
        Notes: []
      };
    }
    // Support both old (Dimensions) and new (Notes) structure for backward compatibility
    const content = skillForm.content;
    if (content.Dimensions && !content.Notes) {
      content.Notes = content.Dimensions;
      delete content.Dimensions;
    }
    return content;
  };

  const contentStructure = getContentStructure();
  
  console.log('[CourtDimensionsForm] Rendering with:', { 
    skillForm, 
    contentStructure,
    imagesCount: contentStructure.Images?.length || 0,
    videosCount: contentStructure.Videos?.length || 0,
    textCount: contentStructure.Text?.length || 0,
    notesCount: contentStructure.Notes?.length || 0
  });

  // Add new image
  const addImage = () => {
    const newImage = { title: '', url: '', Author: '', textBefore: '', textAfter: '' };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Images: [...(contentStructure.Images || []), newImage]
      }
    });
  };

  // Update image
  const updateImage = (index: number, field: string, value: string) => {
    const updatedImages = [...(contentStructure.Images || [])];
    updatedImages[index] = { ...updatedImages[index], [field]: value };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Images: updatedImages
      }
    });
  };

  // Remove image
  const removeImage = (index: number) => {
    const updatedImages = (contentStructure.Images || []).filter((_: any, i: number) => i !== index);
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Images: updatedImages
      }
    });
  };

  // Add new video
  const addVideo = () => {
    const newVideo = { title: '', url: '', Author: '', textBefore: '', textAfter: '', videoType: 'youtube' };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Videos: [...(contentStructure.Videos || []), newVideo]
      }
    });
  };

  // Update video
  const updateVideo = (index: number, field: string, value: string) => {
    const updatedVideos = [...(contentStructure.Videos || [])];
    updatedVideos[index] = { ...updatedVideos[index], [field]: value };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Videos: updatedVideos
      }
    });
  };

  // Remove video
  const removeVideo = (index: number) => {
    const updatedVideos = (contentStructure.Videos || []).filter((_: any, i: number) => i !== index);
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Videos: updatedVideos
      }
    });
  };

  // Add new text
  const addText = () => {
    const newText = { title: '', content: '', Author: '' };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Text: [...(contentStructure.Text || []), newText]
      }
    });
  };

  // Update text
  const updateText = (index: number, field: string, value: string) => {
    const updatedTexts = [...(contentStructure.Text || [])];
    updatedTexts[index] = { ...updatedTexts[index], [field]: value };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Text: updatedTexts
      }
    });
  };

  // Remove text
  const removeText = (index: number) => {
    const updatedTexts = (contentStructure.Text || []).filter((_: any, i: number) => i !== index);
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Text: updatedTexts
      }
    });
  };

  // Add new note object
  const addNote = () => {
    const newNote = { NoteTitle: '', Value: {} };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Notes: [...(contentStructure.Notes || []), newNote]
      }
    });
  };

  // Update note title
  const updateNoteTitle = (index: number, title: string) => {
    const updatedNotes = [...(contentStructure.Notes || [])];
    updatedNotes[index] = { ...updatedNotes[index], NoteTitle: title };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Notes: updatedNotes
      }
    });
  };

  // Add key-value pair to note
  const addNoteKeyValue = (noteIndex: number) => {
    const updatedNotes = [...(contentStructure.Notes || [])];
    const currentValue = updatedNotes[noteIndex].Value || {};
    updatedNotes[noteIndex] = {
      ...updatedNotes[noteIndex],
      Value: { ...currentValue, '': '' }
    };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Notes: updatedNotes
      }
    });
  };

  // Update note key-value pair
  const updateNoteKeyValue = (noteIndex: number, oldKey: string, newKey: string, newValue: string) => {
    const updatedNotes = [...(contentStructure.Notes || [])];
    const currentValue = { ...(updatedNotes[noteIndex].Value || {}) };
    
    if (oldKey !== newKey) {
      delete currentValue[oldKey];
    }
    currentValue[newKey] = newValue;
    
    updatedNotes[noteIndex] = {
      ...updatedNotes[noteIndex],
      Value: currentValue
    };
    
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Notes: updatedNotes
      }
    });
  };

  // Remove note key-value pair
  const removeNoteKeyValue = (noteIndex: number, key: string) => {
    const updatedNotes = [...(contentStructure.Notes || [])];
    const currentValue = { ...(updatedNotes[noteIndex].Value || {}) };
    delete currentValue[key];
    updatedNotes[noteIndex] = {
      ...updatedNotes[noteIndex],
      Value: currentValue
    };
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Notes: updatedNotes
      }
    });
  };

  // Remove note object
  const removeNote = (index: number) => {
    const updatedNotes = (contentStructure.Notes || []).filter((_: any, i: number) => i !== index);
    setSkillForm({
      ...skillForm,
      content: {
        ...contentStructure,
        Notes: updatedNotes
      }
    });
  };

  return (
    <Grid container spacing={3}>
      {/* Title Field */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title *"
          value={skillForm.title || ''}
          onChange={(e) => setSkillForm({ ...skillForm, title: e.target.value })}
          required
        />
      </Grid>
      
      {/* Description Field */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          value={skillForm.description || ''}
          onChange={(e) => setSkillForm({ ...skillForm, description: e.target.value })}
          multiline
          rows={3}
          placeholder="Brief description or overview"
        />
      </Grid>
      
      {/* Explanation Field */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Explanation"
          value={skillForm.explanation || ''}
          onChange={(e) => setSkillForm({ ...skillForm, explanation: e.target.value })}
          multiline
          rows={5}
          placeholder="Detailed explanation or additional information"
        />
      </Grid>
      
      {/* Images Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Images</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addImage}>
              Add Image
            </Button>
          </Box>
          {(contentStructure.Images || []).map((image: any, index: number) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">Image {index + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removeImage(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Text Before Image"
                    value={image.textBefore || ''}
                    onChange={(e) => updateImage(index, 'textBefore', e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Optional text to display before this image"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={image.title || ''}
                    onChange={(e) => updateImage(index, 'title', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="URL"
                    value={image.url || ''}
                    onChange={(e) => updateImage(index, 'url', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Author/Source"
                    value={image.Author || ''}
                    onChange={(e) => updateImage(index, 'Author', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Text After Image"
                    value={image.textAfter || ''}
                    onChange={(e) => updateImage(index, 'textAfter', e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Optional text to display after this image"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Videos Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Videos</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addVideo}>
              Add Video
            </Button>
          </Box>
          {(contentStructure.Videos || []).map((video: any, index: number) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">Video {index + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removeVideo(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Text Before Video"
                    value={video.textBefore || ''}
                    onChange={(e) => updateVideo(index, 'textBefore', e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Optional text to display before this video"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={video.title || ''}
                    onChange={(e) => updateVideo(index, 'title', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Video Type"
                    value={video.videoType || 'youtube'}
                    onChange={(e) => updateVideo(index, 'videoType', e.target.value)}
                    size="small"
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="youtube">YouTube</option>
                    <option value="vimeo">Vimeo</option>
                    <option value="custom">Custom</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="URL"
                    value={video.url || ''}
                    onChange={(e) => updateVideo(index, 'url', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Author/Source"
                    value={video.Author || ''}
                    onChange={(e) => updateVideo(index, 'Author', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Text After Video"
                    value={video.textAfter || ''}
                    onChange={(e) => updateVideo(index, 'textAfter', e.target.value)}
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Optional text to display after this video"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Text Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Text Content</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addText}>
              Add Text
            </Button>
          </Box>
          {(contentStructure.Text || []).map((text: any, index: number) => (
            <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">Text {index + 1}</Typography>
                <IconButton size="small" color="error" onClick={() => removeText(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={text.title || ''}
                    onChange={(e) => updateText(index, 'title', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Content"
                    value={text.content || ''}
                    onChange={(e) => updateText(index, 'content', e.target.value)}
                    multiline
                    rows={6}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Author/Source"
                    value={text.Author || ''}
                    onChange={(e) => updateText(index, 'Author', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Paper>
      </Grid>

      {/* Notes Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notes</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addNote}>
              Add Note
            </Button>
          </Box>
          {(contentStructure.Notes || []).map((note: any, noteIndex: number) => (
            <Box key={noteIndex} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <TextField
                  label="Note Title"
                  value={note.NoteTitle || ''}
                  onChange={(e) => updateNoteTitle(noteIndex, e.target.value)}
                  size="small"
                  sx={{ flex: 1, mr: 2 }}
                  placeholder="e.g., Important Points, Key Information"
                />
                <IconButton size="small" color="error" onClick={() => removeNote(noteIndex)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Key-Value Pairs</Typography>
                {Object.entries(note.Value || {}).map(([key, value], kvIndex) => (
                  <Grid container spacing={2} key={kvIndex} sx={{ mb: 1 }}>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Key"
                        value={key}
                        onChange={(e) => updateNoteKeyValue(noteIndex, key, e.target.value, value as string)}
                        size="small"
                        placeholder="e.g., Point, Detail, Item"
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        label="Value"
                        value={value as string}
                        onChange={(e) => updateNoteKeyValue(noteIndex, key, key, e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton size="small" color="error" onClick={() => removeNoteKeyValue(noteIndex, key)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addNoteKeyValue(noteIndex)}
                  sx={{ mt: 1 }}
                >
                  Add Key-Value Pair
                </Button>
              </Box>
            </Box>
          ))}
        </Paper>
      </Grid>
      
    </Grid>
  );
};

